import { useLocation } from 'react-router'
import { useState, useEffect, useContext } from 'react'
import { ROUTES } from '@/config/routes'
import { Bars3Icon } from '@heroicons/react/20/solid'
import { DealerContext } from '@/context/DealerContext'
import PersistedLink from './PersistedLink'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { useNavigationValidation } from '@/hooks/useNavigationValidation'

const Header = () => {
  // @Adam : TODO we need to add a class 'text-[var(--nav-text-hover)]' if the user has already completed the form for the section (Ryan wants a step completion/active/uncompleted aproach)
  const navLinkClasses = `relative inline-flex items-center max-lg:h-auto h-full text-[var(--nav-text)] [&.active]:font-[var(--font-weight-bold)] [&.active]:text-[var(--nav-text-hover)] hover:text-[var(--nav-text-hover)]
   text-center text-base transition duration-300 laeding-[1] max-lg:border-b max-lg:border-b-4 border-transparent max-lg:[&.active]:border-[var(--nav-text-hover)] pt-1 max-xl:py-6 max-xl:px-8
   [&.active]:before:content-[""] [&.active]:before:block [&.active]:before:absolute [&.active]:before:left-0 [&.active]:before:bottom-0
   [&.active]:before:w-full [&.active]:before:h-1 [&.active]:before:rounded-sm [&.active]:before:bg-[var(--nav-text-hover)] max-lg:[&.active]:before:hidden`
  const navLinkIndicatorClasses = 'inline-flex items-center h-full font-[var(--font-weight-bold)] text-[var(--nav-text)] text-[var(--nav-text-hover)] hover:text-[var(--nav-text-hover)] text-center text-sm sm:text-base transition duration-300 laeding-[1] border-b-3 border-[var(--nav-text-hover)]'
  const [mobileMenu, setMobileMenu] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dealerContext = useContext(DealerContext)
  const navigate = usePersistedNavigation()
  const { validateNavigationWithDirection } = useNavigationValidation()

  const handleMenuToggle = () => {
    setMobileMenu(!mobileMenu)
  }

  const handleNavigation = (path: string) => {
    if (validateNavigationWithDirection(location.pathname, path)) {
      navigate(path)
    } else {
      setMobileMenu(false)
      return false
    }
    return true
  }

  const handleAboutYourBusinessClick = () => handleNavigation(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)
  const handleAboutYouClick = () => handleNavigation(ROUTES.BUSINESS.ABOUT_YOU.path)
  const handleFinancialPositionClick = () => handleNavigation(ROUTES.BUSINESS.FINANCIAL_POSITION.path)
  const handleApplicationOptionsClick = () => handleNavigation(ROUTES.BUSINESS.APPLICATION_OPTIONS.path)

  const getActiveRouteName = () => {
    const route = ROUTES.BUSINESS.ROOT
    const currentRoute = location.pathname
    const routeName = Object.values(ROUTES.BUSINESS).find((route) => route.path === currentRoute)

    return routeName ? routeName.name : route.name
  }

  const activeRouteName = getActiveRouteName()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    handleScroll()

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <header className='text-sm group' data-mobile-menu={mobileMenu || null} data-is-scrolled={isScrolled || null}>
        <div className='fixed top-0 z-20 left-0 w-full px-12 max-xl:px-0 max-xl:shadow-xl transition-all duration-300 group-data-[is-scrolled]:px-0 max-xl:group-data-[mobile-menu]:h-screen'>
          <div className='bg-white overflow-hidden transition-all duration-300 h-[72px] max-lg:group-data-[mobile-menu]:h-screen max-xl:group-data-[mobile-menu]:h-[200px] rounded-full px-4 flex items-start justify-between max-xl:flex-wrap translate-y-4 max-xl:translate-y-0 max-xl:rounded-none group-data-[is-scrolled]:rounded-none group-data-[is-scrolled]:translate-y-0'>
            <PersistedLink to='/' className='w-[120px] md:w-[134px] mr-1 md:mx-6 flex items-center h-[72px]'>
              <img
                className='w-full'
                src={dealerContext?.themeLogo || '/brand/udc/site-logo.png'}
                alt='Logo'
              />
            </PersistedLink>
            <div className='xl:hidden h-[72px] flex justify-center grow max-sm:max-w-[94px]'>
              <span className={navLinkIndicatorClasses}>
                {activeRouteName}
              </span>
            </div>
            <nav className='grow flex flex-col lg:flex-row justify-evenly gap-7 h-[72px] max-lg:justify-start max-xl:h-auto max-xl:w-full max-xl:order-last max-xl:top-20 max-xl:left-0 max-xl:right-0 max-xl:opacity-0 max-xl:group-data-[mobile-menu]:delay-300 max-xl:group-data-[mobile-menu]:opacity-100 max-xl:pt-4 max-lg:pt-10 transition max-lg:group-data-[mobile-menu]:duration-500 max-lg:overflow-auto max-lg:h-[calc(100dvh-72px)] max-lg:-mx-4'>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path} 
                isNavLink
                onClick={handleAboutYourBusinessClick}
              >
                {ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.BUSINESS.ABOUT_YOU.path} 
                isNavLink
                onClick={handleAboutYouClick}
              >
                {ROUTES.BUSINESS.ABOUT_YOU.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.BUSINESS.FINANCIAL_POSITION.path} 
                isNavLink
                onClick={handleFinancialPositionClick}
              >
                {ROUTES.BUSINESS.FINANCIAL_POSITION.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.BUSINESS.APPLICATION_OPTIONS.path} 
                isNavLink
                onClick={handleApplicationOptionsClick}
              >
                {ROUTES.BUSINESS.APPLICATION_OPTIONS.name}
              </PersistedLink>
            </nav>
            <div className='w-[120px] md:w-[134px] h-[72px] flex items-center justify-end gap-2 ml-1 xl:hidden'>
              <button
                className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)] '
                onClick={handleMenuToggle}
              >
                <Bars3Icon className='w-6 h-6 relative z-10' aria-hidden='true' onClick={handleMenuToggle} />
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className='h-20'></div>
    </>
  )
}

export { Header }
